import axios from 'axios';
import { BarChart3, Briefcase, Building, CreditCard, Mail, MapPin, Mic, Phone, Rocket, Settings as SettingsIcon, TrendingUp, Users } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';

import { useAuth } from '../context/AuthContext';
import { auth } from '../firebase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/metronic/ui/Card';
import { Button } from '../components/metronic/ui/Button';

// Metronic Components - Consistent Card System
import {
  ActionButton,
  ActionCard,
  DataCard,
  InfoCard,
  InfoItem,
  MetricCard,
  WelcomeCard
} from '../metronic/components';

// Default avatar fallback
const DEFAULT_AVATAR = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%236B7280'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z'/%3E%3C/svg%3E";

// Function to validate image URL
const isValidImageUrl = (url) => {
  if (!url) return false;

  // Check if it's a data URL (always valid)
  if (url.startsWith('data:')) return true;

  // Check if it's a LinkedIn URL (likely to be expired)
  if (url.includes('licdn.com') || url.includes('linkedin.com')) return false;

  // Check if it's a valid URL format
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export default function DashboardNew() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Loading states
  const [loading, setLoading] = useState({
    metrics: true,
    activities: true,
    analytics: true,
    positions: true
  });

  // State for metrics
  const [metrics, setMetrics] = useState({
    openPositions: 0,
    totalApplications: 0,
    totalCalls: 0,
    totalEmails: 0
  });

  // State for latest activities with lazy loading
  const [latestActivities, setLatestActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const activitiesLoadingRef = useRef(false);
  const [hasMoreActivities, setHasMoreActivities] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [activitiesPerPage] = useState(10);
  const activitiesContainerRef = useRef(null);

  // State for analytics
  const [analytics, setAnalytics] = useState({
    thisWeek: [],
    lastWeek: []
  });

  // State for top positions
  const [topPositions, setTopPositions] = useState([]);

  // State for credits
  const [creditStatus, setCreditStatus] = useState({
    current_credits: 0,
    job_credit_cost: 10,
    can_upload_job: false,
    credits_needed: 0,
    jobs_possible: 0,
    loading: true
  });

  // State for client profile (fetched from backend)
  const [clientProfile, setClientProfile] = useState({
    companyName: '',
    email: '',
    industry: '',
    location: ''
  });

  // Cache for job details to avoid N+1 queries
  const jobDetailsCache = useRef(new Map());
  const isDevMockAuth = process.env.NODE_ENV === 'development' && !!currentUser && !auth.currentUser;

  const getAuthHeaders = useCallback(async () => {
    if (auth.currentUser) {
      const idToken = await auth.currentUser.getIdToken();
      return { Authorization: `Bearer ${idToken}` };
    }
    // Dev mock auth mode: backend demo mode can bypass token verification
    if (isDevMockAuth) {
      return {};
    }
    return {};
  }, [isDevMockAuth]);

  // Fetch job details with caching
  const fetchJobDetails = useCallback(async (jobId) => {
    if (jobDetailsCache.current.has(jobId)) {
      return jobDetailsCache.current.get(jobId);
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/get_job_description/${jobId}`);
      const jobTitle = response.data?.title || 'Unknown Position';
      jobDetailsCache.current.set(jobId, jobTitle);
      return jobTitle;
    } catch (error) {
      console.error('Error fetching job details:', error);
      const fallbackTitle = 'Unknown Position';
      jobDetailsCache.current.set(jobId, fallbackTitle);
      return fallbackTitle;
    }
  }, []);

  // Fetch activities with lazy loading
  const fetchLatestActivities = useCallback(async (page = 1, append = false) => {
    if (activitiesLoadingRef.current) return;

    activitiesLoadingRef.current = true;
    setActivitiesLoading(true);
    try {
      console.log(`Fetching activities page ${page}...`);

      // Wait for user context; in dev mock mode auth.currentUser can be null by design
      if (!currentUser) {
        console.log('fetchLatestActivities: No authenticated user, skipping API call');
        setActivitiesLoading(false);
        activitiesLoadingRef.current = false;
        return;
      }
      const headers = await getAuthHeaders();

      // Try both localhost and actual server address
      let response;
      try {
        response = await axios.get(`${API_BASE_URL}/api/dashboard/latest-activities?page=${page}&limit=${activitiesPerPage}`, {
          headers
        });
        console.log('Local server response:', response.data);
      } catch (localError) {
        console.warn('Could not connect to local server, trying remote server...');
        response = await axios.get(`/api/dashboard/latest-activities?page=${page}&limit=${activitiesPerPage}`, {
          headers
        });
        console.log('Remote server response:', response.data);
      }

      if (response.data.success && response.data.activities && response.data.activities.length > 0) {
        // Process activities in batches to avoid blocking UI
        const processActivities = async (activities) => {
          const processedActivities = [];

          for (let i = 0; i < activities.length; i++) {
            const activity = activities[i];

            // Default values
            let contactedForJob = 'Unknown Position';

            // If we have evaluated_jobs array, fetch the job title for the latest job
            if (activity.evaluated_jobs && activity.evaluated_jobs.length > 0) {
              const lastJobId = activity.evaluated_jobs[activity.evaluated_jobs.length - 1];
              contactedForJob = await fetchJobDetails(lastJobId);
            }

            const processedActivity = {
              id: activity.id || String(Math.random()),
              name: activity.candidateName || 'Unknown Candidate',
              avatar: isValidImageUrl(activity.candidateImage) ? activity.candidateImage : DEFAULT_AVATAR,
              job: activity.jobTitle || contactedForJob,
              status: activity.status || 'pending',
              date: activity.timestamp || new Date().toISOString(),
              type: activity.activityType || 'Call'
            };

            processedActivities.push(processedActivity);

            // Update UI every 3 activities to show progress
            if (i % 3 === 0 && i > 0) {
              setLatestActivities(prev =>
                append ? [...prev, ...processedActivities.slice(-3)] : processedActivities
              );
            }
          }

          return processedActivities;
        };

        const processedActivities = await processActivities(response.data.activities);

        setLatestActivities(prev =>
          append ? [...prev, ...processedActivities] : processedActivities
        );

        // Check if there are more activities
        setHasMoreActivities(response.data.activities.length === activitiesPerPage);
        setCurrentPage(page);
      } else {
        console.log('No activities found or success is false');
        if (!append) {
          setLatestActivities([]);
        }
        setHasMoreActivities(false);
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
      if (!append) {
        setLatestActivities([]);
      }
    } finally {
      setActivitiesLoading(false);
      activitiesLoadingRef.current = false;
      setLoading(prev => ({ ...prev, activities: false }));
    }
  }, [currentUser, getAuthHeaders, activitiesPerPage, fetchJobDetails]);

  // Load more activities when user scrolls to bottom
  const handleScroll = useCallback(() => {
    if (!activitiesContainerRef.current || activitiesLoading || !hasMoreActivities) return;

    const { scrollTop, scrollHeight, clientHeight } = activitiesContainerRef.current;
    const threshold = 100; // pixels from bottom

    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      console.log('Loading more activities...');
      fetchLatestActivities(currentPage + 1, true);
    }
  }, [currentPage, activitiesLoading, hasMoreActivities, fetchLatestActivities]);

  // Add scroll listener for lazy loading
  useEffect(() => {
    const container = activitiesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Fetch metrics with loading state
  const fetchMetrics = useCallback(async () => {
    setLoading(prev => ({ ...prev, metrics: true }));
    try {
      // Wait for context user; auth.currentUser can be null in dev mock auth mode
      if (!currentUser) {
        console.log('fetchMetrics: No authenticated user, skipping API call');
        setMetrics({ openPositions: 0, totalApplications: 0, totalCalls: 0, totalEmails: 0 });
        setLoading(prev => ({ ...prev, metrics: false }));
        return;
      }
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API_BASE_URL}/api/dashboard/metrics`, {
        headers
      });
      if (response.data.success) {
        setMetrics({
          openPositions: response.data.data.openPositions || 0,
          totalApplications: response.data.data.totalApplications || 0,
          totalCalls: response.data.data.totalCalls || 0,
          totalEmails: response.data.data.totalEmails || 0
        });
      }
    } catch (error) {
      console.error('Error fetching metrics:', error);
      setMetrics({
        openPositions: 0,
        totalApplications: 0,
        totalCalls: 0,
        totalEmails: 0
      });
    } finally {
      setLoading(prev => ({ ...prev, metrics: false }));
    }
  }, [currentUser, getAuthHeaders]);

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      // Wait for context user; auth.currentUser can be null in dev mock auth mode
      if (!currentUser) {
        console.log('fetchAnalytics: No authenticated user, skipping API call');
        setAnalytics({ thisWeek: [], lastWeek: [] });
        return;
      }
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API_BASE_URL}/api/dashboard/analytics`, {
        headers
      });
      if (response.data.success) {
        setAnalytics({
          thisWeek: response.data.data.thisWeek || [],
          lastWeek: response.data.data.lastWeek || []
        });
      } else {
        console.error('Failed to fetch analytics:', response.data.error);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setAnalytics({ thisWeek: [], lastWeek: [] });
    }
  }, [currentUser, getAuthHeaders]);

  // Fetch top positions
  const fetchTopPositions = useCallback(async () => {
    try {
      // Wait for context user; auth.currentUser can be null in dev mock auth mode
      if (!currentUser) {
        console.log('fetchTopPositions: No authenticated user, skipping API call');
        setTopPositions([]);
        return;
      }
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API_BASE_URL}/api/dashboard/top-positions`, {
        headers
      });
      if (response.data.success && response.data.data?.positions) {
        setTopPositions(response.data.data.positions);
      } else {
        setTopPositions([]);
      }
    } catch (error) {
      console.error('Error fetching top positions:', error);
      setTopPositions([]);
    }
  }, [currentUser, getAuthHeaders]);

  // Fetch credit status
  const fetchCreditStatus = useCallback(async () => {
    try {
      // Wait for context user; auth.currentUser can be null in dev mock auth mode
      if (!currentUser) {
        console.log('fetchCreditStatus: No authenticated user, skipping API call');
        setCreditStatus(prev => ({ ...prev, loading: false }));
        return;
      }
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API_BASE_URL}/api/credits/status`, {
        headers
      });

      if (response.data) {
        setCreditStatus({
          ...response.data,
          loading: false
        });
      }
    } catch (error) {
      console.error('Error fetching credit status:', error);
      console.error('Error response:', error.response?.data);
      setCreditStatus(prev => ({ ...prev, loading: false }));
    }
  }, [currentUser, getAuthHeaders]);

  // Fetch client profile
  const fetchClientProfile = useCallback(async () => {
    try {
      // Wait for context user; auth.currentUser can be null in dev mock auth mode
      if (!currentUser) {
        console.log('fetchClientProfile: No authenticated user, skipping API call');
        return;
      }
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API_BASE_URL}/api/settings/profile`, {
        headers
      });

      if (response.data && response.data.account) {
        setClientProfile({
          companyName: response.data.account.companyName || '',
          email: response.data.account.email || '',
          industry: response.data.account.industry || '',
          location: response.data.account.location || ''
        });
      }
    } catch (error) {
      console.error('Error fetching client profile:', error);
    }
  }, [currentUser, getAuthHeaders]);

  // Purchase credits
  const handlePurchaseCredits = useCallback(async (creditPackage = 100) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        alert('User not authenticated. Please log in again.');
        return;
      }
      const idToken = await user.getIdToken();
      const response = await axios.post(`${API_BASE_URL}/api/credits/purchase`,
        { credit_package: creditPackage },
        {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        }
      );

      if (response.data.success) {
        // Refresh credit status after purchase
        await fetchCreditStatus();
        alert(`Successfully purchased ${creditPackage} credits!`);
      } else {
        alert('Failed to purchase credits: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error purchasing credits:', error);
      alert('Failed to purchase credits. Please try again.');
    }
  }, [fetchCreditStatus]);

  useEffect(() => {
    // Only fetch data if user is authenticated
    if (currentUser) {
      // Initial fetch
      fetchMetrics();
      fetchLatestActivities(1, false); // Start with page 1
      fetchAnalytics();
      fetchTopPositions();
      fetchCreditStatus();
      fetchClientProfile(); // Fetch client profile from settings

      // Set up polling for fresh data every 5 minutes (only when tab is visible)
      // COST OPTIMIZATION: Reduced from 30 seconds to 5 minutes, only polls when tab is active
      const intervalId = setInterval(() => {
        if (!document.hidden) { // Only poll when tab is visible
          console.log('Polling for fresh data...');
          fetchLatestActivities(1, false);
          fetchCreditStatus(); // Also refresh credit status
          fetchClientProfile(); // Also refresh client profile
        }
      }, 300000); // 5 minutes instead of 30 seconds

      // Also refresh when tab becomes visible
      const handleVisibilityChange = () => {
        if (!document.hidden && currentUser) {
          fetchLatestActivities(1, false);
          fetchCreditStatus();
          fetchClientProfile();
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Cleanup interval and event listener on component unmount
      return () => {
        clearInterval(intervalId);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [currentUser, fetchMetrics, fetchLatestActivities, fetchAnalytics, fetchTopPositions, fetchCreditStatus, fetchClientProfile]);

  const handleLaunchAlice = () => {
    navigate('/conversation');
  };

  // Show loading if user authentication is still in progress
  if (!currentUser) {
    return (
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Welcome to ALICE
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  Your AI Recruiter Assistant
                </p>
              </div>
              {!creditStatus.loading && (
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <div className={`text-lg font-semibold ${
                      creditStatus.current_credits >= creditStatus.job_credit_cost
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {creditStatus.current_credits} Credits
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {creditStatus.jobs_possible} jobs available
                    </div>
                  </div>
                  {!creditStatus.can_upload_job && (
                    <button
                      onClick={() => handlePurchaseCredits(100)}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                    >
                      Buy Credits
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Metric Cards - Metronic Styled */}
          <div className="grid gap-6 mb-8 md:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              title="Open Positions"
              value={metrics.openPositions}
              color="orange"
              loading={loading.metrics}
              icon={Briefcase}
            />

            <MetricCard
              title="Total Applications"
              value={metrics.totalApplications}
              color="green"
              loading={loading.metrics}
              icon={Users}
            />

            <MetricCard
              title="Calls Completed"
              value={metrics.totalCalls}
              color="blue"
              loading={loading.metrics}
              icon={Phone}
            />

            <MetricCard
              title="Emails Sent"
              value={metrics.totalEmails}
              color="purple"
              loading={loading.metrics}
              icon={Mail}
            />
          </div>

          {/* Autonomous Campaign Widget - Metronic Card (User Access style) */}
          <div className="mb-8">
            <Card className="border border-gray-200/80 shadow-xs">
              <CardContent className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-6">
                {/* Left: Icon + title + description */}
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-primary-50 flex items-center justify-center text-primary-600">
                    <Rocket className="h-5 w-5" />
                  </div>
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <CardTitle className="text-base sm:text-lg">
                        Autonomous Campaigns
                      </CardTitle>
                      <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                        New
                      </span>
                    </div>
                    <CardDescription className="text-sm max-w-xl">
                      Let ALICE handle the entire campaign workflow. Set your goal, refine requirements,
                      and watch ALICE autonomously source, evaluate, contact, and schedule candidates.
                    </CardDescription>
                  </div>
                </div>

                {/* Right: actions */}
                <div className="flex flex-col items-stretch sm:flex-row sm:items-center gap-2 sm:gap-3 w-full md:w-auto">
                  <Button
                    onClick={() => navigate('/campaigns/autonomous/new')}
                    variant="primary"
                    size="md"
                    className="sm:min-w-[150px] bg-[#0f7dd1] hover:bg-[#0d6bb8] text-white"
                  >
                    Start Campaign
                  </Button>
                  <Button
                    onClick={() => navigate('/campaigns/autonomous')}
                    variant="primary"
                    size="md"
                    className="sm:min-w-[150px] bg-[#f7911d] hover:bg-[#e07a0c] text-white"
                  >
                    View campaigns
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions and Profile Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Client Profile Card - Metronic InfoCard */}
            <div className="lg:col-span-1">
              <InfoCard title="Company Profile">
                <InfoItem
                  label="Company Name"
                  value={clientProfile.companyName}
                  icon={Building}
                />
                <InfoItem
                  label="Email"
                  value={clientProfile.email || currentUser?.email}
                  icon={Mail}
                />
                <InfoItem
                  label="Industry"
                  value={clientProfile.industry}
                  icon={TrendingUp}
                />
                <InfoItem
                  label="Location"
                  value={clientProfile.location}
                  icon={MapPin}
                />
              </InfoCard>
            </div>

            {/* Credits Card - Metronic InfoCard */}
            <div className="lg:col-span-1">
              <InfoCard title="Credits">
                {creditStatus.loading ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : (
                  <div className="text-center space-y-4">
                    <div>
                      <div className={`text-4xl font-bold ${
                        creditStatus.current_credits >= creditStatus.job_credit_cost
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {creditStatus.current_credits}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        Available Credits
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {creditStatus.jobs_possible} jobs possible
                      </p>
                      {!creditStatus.can_upload_job && (
                        <p className="text-xs text-destructive mt-2">
                          Need {creditStatus.credits_needed} more credits
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <ActionButton
                        onClick={() => handlePurchaseCredits(100)}
                        variant="primary"
                        icon={CreditCard}
                        className="bg-[#f7911d] hover:bg-[#e07a0c] text-white"
                      >
                        Purchase 100 Credits
                      </ActionButton>
                      <ActionButton
                        onClick={() => handlePurchaseCredits(50)}
                        variant="outline"
                        className="border-[#f7911d] text-[#f7911d] hover:bg-[#fff7ed]"
                      >
                        Purchase 50 Credits
                      </ActionButton>
                    </div>
                  </div>
                )}
              </InfoCard>
            </div>

            {/* Quick Actions Card - Metronic ActionCard */}
            <div className="lg:col-span-1">
              <ActionCard title="Quick Actions">
                <ActionButton
                  onClick={handleLaunchAlice}
                  variant="primary"
                  icon={Mic}
                  className="bg-[#0f7dd1] hover:bg-[#0d6bb8] text-white"
                >
                  Launch ALICE
                </ActionButton>
                <ActionButton
                  onClick={() => navigate('/talent-insights')}
                  variant="secondary"
                  icon={TrendingUp}
                  className="bg-[#0f7dd1]/10 text-[#0f7dd1] hover:bg-[#0f7dd1]/15"
                >
                  Market Intelligence
                </ActionButton>
                <ActionButton
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  icon={BarChart3}
                  className="border-gray-200 text-gray-700 hover:bg-gray-50"
                >
                  View Analytics
                </ActionButton>
                <ActionButton
                  onClick={() => navigate('/settings')}
                  variant="outline"
                  icon={SettingsIcon}
                  className="border-gray-200 text-gray-700 hover:bg-gray-50"
                >
                  Settings
                </ActionButton>
              </ActionCard>
            </div>
          </div>

          {/* Recent Activities Table with Lazy Loading - Metronic DataCard */}
          <DataCard
            title="Recent Activities"
            loading={loading.activities && latestActivities.length === 0}
            emptyMessage="No recent activities found"
          >
            {latestActivities.length > 0 && (
              <div
                ref={activitiesContainerRef}
                className="overflow-y-auto max-h-96"
              >
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Candidate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Position
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {latestActivities.map((activity) => (
                      <tr key={activity.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <img
                                className="h-10 w-10 rounded-full"
                                src={activity.avatar}
                                alt=""
                              />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {activity.name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-white">{activity.job}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-white">{activity.type}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            activity.status === 'completed' || activity.status === 'Completed'
                              ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100'
                          }`}>
                            {activity.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {new Date(activity.date).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Loading indicator for lazy loading */}
                {activitiesLoading && latestActivities.length > 0 && (
                  <div className="p-4 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-2 text-sm text-muted-foreground">Loading more activities...</p>
                  </div>
                )}

                {/* End of list indicator */}
                {!hasMoreActivities && latestActivities.length > 0 && (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    No more activities to load
                  </div>
                )}
              </div>
            )}
          </DataCard>

          {/* Getting Started - Metronic WelcomeCard */}
          <div className="mt-8">
            <WelcomeCard
              title="Getting Started"
              message="Welcome to ALICE! Your AI recruiter assistant is ready to help you find the perfect candidates. Click 'Launch ALICE' to start a conversation and begin your recruitment process."
              tip="You can use voice or text to communicate with ALICE"
              emoji="🎤"
              variant="info"
            />
          </div>
        </div>
    </div>
  );
}
